/**
 * Holy Tea · Juliet mug upsell components
 *
 * <ht-juliet-mug-selector>  B2B package product page: pick Juliet colours and
 *                           quantities, see live tier pricing, and add the
 *                           package + all chosen mugs in ONE /cart/add.js call.
 * <ht-juliet-upsell>        B2C subscription landing page: add the (Subi)
 *                           subscription plus an optional one-time Juliet mug.
 *
 * IMPORTANT: this code only DISPLAYS the upsell discount (tier % from block
 * settings; flat when tier1===tier2). It never changes prices. The actual discount MUST be
 * applied by a Shopify Discount Function or a Shopify discount (not part of
 * this theme). Original prices always come from the real variant price that
 * Liquid renders into data attributes.
 *
 * After adding, the theme's own mechanism is used to refresh the cart drawer
 * and cart bubble: the bundled section HTML for every <cart-items-component>
 * is requested with the add call and a CartAddEvent (ThemeEvents.cartUpdate)
 * is dispatched, exactly like product-form.js does.
 */
import { CartAddEvent, CartErrorEvent } from '@theme/events';

/**
 * @param {number} cents
 * @param {string} currency
 * @param {string} locale
 */
function formatMoney(cents, currency, locale) {
  try {
    return new Intl.NumberFormat(locale || undefined, { style: 'currency', currency: currency || 'EUR' }).format(
      cents / 100
    );
  } catch (error) {
    return (cents / 100).toFixed(2);
  }
}

/**
 * Discounted unit price in cents, rounded like Liquid's `round` (half up).
 * @param {number} priceCents
 * @param {number} percent
 */
function discounted(priceCents, percent) {
  return Math.round((priceCents * (100 - percent)) / 100);
}

/** @param {HTMLElement} root */
function readStrings(root) {
  const node = root.querySelector('script[data-ht-strings]');
  if (!node) return {};
  try {
    return JSON.parse(node.textContent || '{}');
  } catch (error) {
    return {};
  }
}

/**
 * Adds several lines to the cart in one request and lets the theme refresh
 * the cart drawer / cart page / cart bubble.
 * @param {HTMLElement} source - element that dispatches the cart event
 * @param {Array<Record<string, any>>} items - /cart/add.js items
 */
async function addItemsToCart(source, items) {
  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
    .filter(Boolean);

  /** @type {Record<string, any>} */
  const body = { items };
  if (sectionIds.length) body.sections = Array.from(new Set(sectionIds)).join(',');

  // `Theme` is declared by snippets/scripts.liquid as a global `const` (not on window);
  // its routes carry the locale prefix (e.g. /nl/cart/add.js) so the returned
  // section HTML is rendered in the shopper's language.
  const routes = (typeof Theme !== 'undefined' && Theme.routes) || {};
  const addUrl = routes.cart_add_url || '/cart/add.js';
  const cartUrl = `${routes.cart_url || '/cart'}.js`;

  const response = await fetch(addUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify(body),
  });
  const data = await response.json();

  if (!response.ok || data.status) {
    source.dispatchEvent(new CartErrorEvent(source.id, data.message, data.description, data.errors));
    const error = new Error(data.description || data.message || 'Cart error');
    throw error;
  }

  let itemCount = 0;
  try {
    const cart = await fetch(cartUrl).then((res) => res.json());
    itemCount = cart.item_count;
  } catch (error) {
    itemCount = items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  }

  source.dispatchEvent(
    new CartAddEvent({}, source.id, {
      source: 'ht-juliet',
      itemCount,
      sections: data.sections,
    })
  );

  return data;
}

/**
 * B2B: Juliet mug selector on the package product page.
 */
class HtJulietMugSelector extends HTMLElement {
  connectedCallback() {
    this.strings = readStrings(this);
    this.tier1 = Number(this.dataset.tier1Percent || 20);
    this.tier2 = Number(this.dataset.tier2Percent || 25);
    this.threshold = Number(this.dataset.bulkThreshold || 6);
    this.flatTier = this.dataset.flatTier === 'true' || this.tier1 === this.tier2;
    this.currency = this.dataset.currency || 'EUR';
    this.locale = this.dataset.locale || document.documentElement.lang;
    this.rows = Array.from(this.querySelectorAll('[data-mug-row]'));
    this.button = this.querySelector('[data-ht-submit]');
    this.status = this.querySelector('[data-ht-status]');

    this.addEventListener('click', this.#onClick);
    this.addEventListener('input', this.#onInput);
    this.addEventListener('change', this.#onInput);
    this.update();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('input', this.#onInput);
    this.removeEventListener('change', this.#onInput);
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const sizePick = target.closest('[data-size-pick]');
    if (sizePick instanceof HTMLButtonElement) {
      this.#pickSize(sizePick);
      return;
    }

    const stepButton = target.closest('[data-step]');
    if (stepButton instanceof HTMLButtonElement) {
      const row = stepButton.closest('[data-mug-qty], [data-mug-row], [data-tin-row]');
      const input = row?.querySelector('input[type="number"]');
      if (input instanceof HTMLInputElement) {
        const max = Number(input.max || 99);
        const next = Math.min(max, Math.max(0, (parseInt(input.value, 10) || 0) + Number(stepButton.dataset.step)));
        input.value = String(next);
        this.update();
      }
      return;
    }

    if (target.closest('[data-ht-submit]')) {
      event.preventDefault();
      if (!this.#companyValid()) return;
      const reminder = this.querySelector('dialog[data-ht-reminder]');
      const total = this.selection.reduce((sum, mug) => sum + mug.qty, 0);
      if (reminder instanceof HTMLDialogElement && (total === 0 || this.hasAttribute('data-popup-only'))) {
        this.#openReminder(reminder);
      } else {
        this.submit();
      }
      return;
    }

    if (target.closest('[data-ht-reminder-add]')) {
      event.preventDefault();
      if (this.#showTinStep()) return;
      this.#closeReminder();
      this.submit();
      return;
    }

    if (target.closest('[data-ht-reminder-skip]')) {
      event.preventDefault();
      this.#clearSelection();
      if (this.#showTinStep()) return;
      this.#closeReminder();
      this.submit();
      return;
    }

    if (target.closest('[data-ht-tin-add]')) {
      event.preventDefault();
      this.tinQty = this.#tinInputQty();
      this.#closeReminder();
      this.submit();
      return;
    }

    if (target.closest('[data-ht-tin-skip]')) {
      event.preventDefault();
      this.tinQty = 0;
      this.#closeReminder();
      this.submit();
      return;
    }

    if (target.closest('[data-ht-reminder-close]') || target.matches('dialog[data-ht-reminder]')) {
      event.preventDefault();
      this.#closeReminder();
    }
  };

  /** Switches the popup to the tins step. Returns false when there is no tins step. */
  #showTinStep() {
    const step1 = this.querySelector('[data-ht-step1]');
    const step2 = this.querySelector('[data-ht-step2]');
    if (!(step2 instanceof HTMLElement) || !step2.hidden) return false;
    if (step1 instanceof HTMLElement) step1.hidden = true;
    step2.hidden = false;
    const panel = step2.closest('.ht-juliet__modal-panel');
    if (panel instanceof HTMLElement) panel.scrollTop = 0;
    const first = step2.querySelector('[data-ht-tin-add]');
    if (first instanceof HTMLElement) first.focus();
    return true;
  }

  #resetSteps() {
    const step1 = this.querySelector('[data-ht-step1]');
    const step2 = this.querySelector('[data-ht-step2]');
    if (step1 instanceof HTMLElement) step1.hidden = false;
    if (step2 instanceof HTMLElement) step2.hidden = true;
  }

  #tinInputQty() {
    const input = this.querySelector('[data-tin-input]');
    if (!(input instanceof HTMLInputElement)) return 0;
    const max = Number(input.max || 40);
    return Math.min(max, Math.max(0, parseInt(input.value, 10) || 0));
  }

  /** @param {HTMLDialogElement} dialog */
  #openReminder(dialog) {
    this.tinQty = 0;
    this.#resetSteps();
    const list = this.querySelector('[data-list-home] > .ht-juliet__mugs');
    const slot = dialog.querySelector('[data-modal-list]');
    if (list && slot) slot.appendChild(list);
    if (!dialog.dataset.bound) {
      dialog.dataset.bound = 'true';
      dialog.addEventListener('close', () => {
        this.#restoreList();
        this.#resetSteps();
      });
    }
    dialog.showModal();
    this.#bindPreview(dialog);
    const rows = [...dialog.querySelectorAll('[data-modal-list] [data-mug-row][data-preview-src]')];
    const picked = rows.find((r) => (parseInt(r.querySelector('input[type="number"]')?.value, 10) || 0) > 0) || rows[0];
    this.#setPreview(dialog, picked, true);
  }

  /** @param {HTMLDialogElement} dialog */
  #bindPreview(dialog) {
    if (dialog.dataset.previewBound) return;
    dialog.dataset.previewBound = 'true';
    const list = dialog.querySelector('[data-modal-list]');
    if (!list) return;
    const pick = (event) => {
      const row = event.target instanceof Element ? event.target.closest('[data-mug-row][data-preview-src]') : null;
      if (row) this.#setPreview(dialog, row);
    };
    list.addEventListener('pointerover', (e) => { if (e.pointerType === 'mouse') pick(e); });
    list.addEventListener('click', pick);
    list.addEventListener('focusin', pick);
  }

  /** @param {HTMLDialogElement} dialog @param {Element|undefined} row @param {boolean} [instant] */
  #setPreview(dialog, row, instant = false) {
    const box = dialog.querySelector('[data-ht-preview]');
    if (!(box instanceof HTMLElement) || !(row instanceof HTMLElement)) return;
    const src = row.dataset.previewSrc;
    if (!src) return;
    dialog.querySelectorAll('[data-modal-list] .is-previewed').forEach((r) => r.classList.remove('is-previewed'));
    row.classList.add('is-previewed');
    box.classList.add('has-image');
    const [a, b] = box.querySelectorAll('img');
    const active = a.classList.contains('is-active') ? a : b;
    if (active.dataset.src === src) return;
    const next = instant ? active : (active === a ? b : a);
    const show = () => {
      if (next !== active) { next.classList.add('is-active'); active.classList.remove('is-active'); }
    };
    next.dataset.src = src;
    if (row.dataset.previewSrcset) next.srcset = row.dataset.previewSrcset;
    next.src = src;
    next.alt = row.dataset.previewAlt || '';
    if (next.complete) show(); else next.addEventListener('load', show, { once: true });
  }

  #restoreList() {
    const home = this.querySelector('[data-list-home]');
    const list = this.querySelector('[data-modal-list] > .ht-juliet__mugs');
    if (home && list) home.appendChild(list);
    this.update();
  }

  #closeReminder() {
    const dialog = this.querySelector('dialog[data-ht-reminder]');
    if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();
    this.#restoreList();
  }

  #clearSelection() {
    for (const row of this.rows) {
      row.querySelectorAll('input[type="number"]').forEach((input) => {
        if (input instanceof HTMLInputElement) input.value = '0';
      });
    }
    this.update();
  }

  /**
   * Quantity units: one per variant. A plain row is its own unit; a colour row with a
   * size toggle ([data-mug-group]) holds one [data-mug-qty] unit per size.
   * @returns {HTMLElement[]}
   */
  get units() {
    return this.rows.flatMap((row) => {
      const subs = /** @type {HTMLElement[]} */ ([...row.querySelectorAll('[data-mug-qty]')]);
      return subs.length ? subs : [row];
    });
  }

  /** @param {HTMLButtonElement} button */
  #pickSize(button) {
    const row = button.closest('[data-mug-row]');
    if (!(row instanceof HTMLElement)) return;
    const id = button.dataset.sizePick || '';
    row.querySelectorAll('[data-size-pick]').forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
    row.querySelectorAll('[data-mug-qty]').forEach((el) => {
      if (el instanceof HTMLElement) el.hidden = el.dataset.sizeId !== id;
    });
    row.querySelectorAll('[data-size-price]').forEach((el) => {
      if (el instanceof HTMLElement) el.hidden = el.dataset.sizePrice !== id;
    });
    if (button.dataset.previewSrc) {
      row.dataset.previewSrc = button.dataset.previewSrc;
      if (button.dataset.previewSrcset) row.dataset.previewSrcset = button.dataset.previewSrcset;
      const dialog = row.closest('dialog');
      if (dialog instanceof HTMLDialogElement && dialog.open) this.#setPreview(dialog, row);
    }
    this.update();
  }

  /** @param {Event} event */
  #onInput = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.hasAttribute('data-plan-radio')) {
      const line = this.querySelector('[data-plan-priceline]');
      if (line && event.target.dataset.line) line.textContent = event.target.dataset.line;
      return;
    }
    // Normalise typed values (empty, negative, decimals, above max) once the user commits them.
    if (event.type === 'change' && event.target instanceof HTMLInputElement && event.target.type === 'number') {
      const input = event.target;
      const max = Number(input.max || 99);
      input.value = String(Math.min(max, Math.max(0, parseInt(input.value, 10) || 0)));
    }
    this.update();
  };

  /** @returns {{id: string, qty: number, price: number}[]} */
  get selection() {
    return this.units
      .map((row) => {
        const input = row.querySelector('input[type="number"]');
        const max = input instanceof HTMLInputElement ? Number(input.max || 99) : 99;
        let qty = input instanceof HTMLInputElement ? parseInt(input.value, 10) || 0 : 0;
        qty = Math.min(max, Math.max(0, qty));
        return { id: row.dataset.variantId || '', qty, price: Number(row.dataset.price || 0), extra: !!row.closest('[data-extra]') };
      })
      .filter((mug) => mug.id);
  }

  update() {
    const s = this.strings;
    const selection = this.selection;
    const total = selection.reduce((sum, mug) => sum + mug.qty, 0);
    const bulk = this.flatTier ? false : total >= this.threshold;
    const percent = this.flatTier ? this.tier1 : bulk ? this.tier2 : this.tier1;
    const money = (/** @type {number} */ cents) => formatMoney(cents, this.currency, this.locale);

    let original = 0;
    let discountedTotal = 0;
    for (const row of this.units) {
      const price = Number(row.dataset.price || 0);
      const unit = discounted(price, percent);
      const scope = row.hasAttribute('data-mug-qty')
        ? row.closest('[data-mug-row]')?.querySelector(`[data-size-price="${row.dataset.sizeId}"]`)
        : row;
      const unitEl = scope?.querySelector('[data-unit-price]');
      const compareEl = scope?.querySelector('[data-compare-price]');
      if (unitEl) unitEl.textContent = money(unit);
      if (compareEl) compareEl.textContent = money(price);
    }
    // Colour rows with sizes: short summary of what is picked, e.g. "300 ml × 2 · 475 ml × 1".
    for (const row of this.rows) {
      const summary = row.querySelector('[data-size-summary]');
      if (!(summary instanceof HTMLElement)) continue;
      const parts = [...row.querySelectorAll('[data-mug-qty]')]
        .map((el) => {
          const input = el.querySelector('input[type="number"]');
          const qty = input instanceof HTMLInputElement ? parseInt(input.value, 10) || 0 : 0;
          return qty > 0 && el instanceof HTMLElement ? `${el.dataset.sizeLabel || ''} × ${qty}` : '';
        })
        .filter(Boolean);
      summary.textContent = parts.join(' · ');
      summary.hidden = parts.length === 0;
    }
    for (const mug of selection) {
      original += mug.price * mug.qty;
      discountedTotal += discounted(mug.price, percent) * mug.qty;
    }

    this.dataset.bulk = String(bulk);
    this.toggleAttribute('data-has-mugs', total > 0);

    const set = (/** @type {string} */ selector, /** @type {string} */ text) => {
      const el = this.querySelector(selector);
      if (el) el.textContent = text;
    };
    const show = (/** @type {string} */ selector, /** @type {boolean} */ visible) => {
      const el = this.querySelector(selector);
      if (el instanceof HTMLElement) el.hidden = !visible;
    };

    // Per-mug price (based on the first mug's real price; all Juliet colours share one price).
    const baseRow = this.units.find((row) => !row.closest('[data-extra]')) || this.units[0];
    const basePrice = baseRow ? Number(baseRow.dataset.price || 0) : 0;
    set('[data-summary-unit]', money(discounted(basePrice, percent)));
    set('[data-summary-compare]', money(basePrice));

    set(
      '[data-summary-count]',
      total === 0
        ? s.noMugs || ''
        : (total === 1 ? s.mugsSelectedOne : s.mugsSelectedOther || '').replace('[count]', String(total))
    );
    set('[data-summary-tier]', this.flatTier ? (s.tierFlat || s.tierStandard || '') : bulk ? s.tierBulk || '' : s.tierStandard || '');
    set('[data-summary-total]', money(discountedTotal));
    set('[data-summary-save]', (s.youSave || '').replace('[amount]', money(original - discountedTotal)));
    show('[data-summary-totals]', total > 0);
    show('[data-summary-count-row]', total > 0);
    show('[data-summary-bulk]', bulk);

    const remaining = this.threshold - total;
    const hint = remaining === 1 ? s.nextTierOne : s.nextTierOther;
    set('[data-summary-hint]', (hint || '').replace('[count]', String(remaining)));
    show('[data-summary-hint]', !this.flatTier && total > 0 && !bulk && remaining > 0);

    if (this.button instanceof HTMLButtonElement && !this.button.hasAttribute('aria-busy')) {
      const label =
        total === 0
          ? s.addPackage
          : (total === 1 ? s.addWithMugsOne : s.addWithMugsOther || '').replace('[count]', String(total));
      const labelEl = this.button.querySelector('[data-label]');
      if (labelEl && label) labelEl.textContent = label;
    }
  }

  /** Reads the theme's product form (variant, quantity, selling plan, properties). */
  #packageLine() {
    const formId = this.dataset.productFormId || '';
    const form = formId ? document.getElementById(formId) : null;

    /** @type {Record<string, any>} */
    const line = {
      id: this.dataset.packageVariantId,
      quantity: 1,
    };
    /** @type {Record<string, string>} */
    const properties = {};

    if (form instanceof HTMLFormElement) {
      const formData = new FormData(form);
      const id = formData.get('id');
      if (id) line.id = String(id);
      const quantity = parseInt(String(formData.get('quantity') || '1'), 10);
      if (quantity > 0 && !this.hasAttribute('data-popup-only')) line.quantity = quantity;
      const sellingPlan = formData.get('selling_plan');
      if (sellingPlan) line.selling_plan = String(sellingPlan);
      for (const [key, value] of formData.entries()) {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && match[1] && typeof value === 'string' && value !== '') properties[match[1]] = value;
      }
    }

    const pickedPlan = this.querySelector('input[data-plan-radio]:checked');
    if (pickedPlan instanceof HTMLInputElement && pickedPlan.value) line.selling_plan = pickedPlan.value;

    if (!line.selling_plan && this.dataset.requiresSellingPlan === 'true' && this.dataset.defaultSellingPlan) {
      line.selling_plan = this.dataset.defaultSellingPlan;
    }
    this.querySelectorAll('[data-ht-prop]').forEach((input) => {
      if (input instanceof HTMLInputElement && input.value.trim() !== '') properties[input.dataset.htProp || input.name] = input.value.trim();
    });
    if (Object.keys(properties).length) line.properties = properties;
    return line;
  }

  /** Required company fields (Trial Box): report the first invalid one. */
  #companyValid() {
    const inputs = Array.from(this.querySelectorAll('[data-ht-prop]'));
    for (const input of inputs) {
      if (!(input instanceof HTMLInputElement)) continue;
      input.value = input.value.trim();
      if (input.inputMode === 'numeric') input.value = input.value.replace(/\s+/g, '');
      if (!input.checkValidity()) {
        input.reportValidity();
        input.focus();
        return false;
      }
    }
    return true;
  }

  async submit() {
    const s = this.strings;
    if (!this.#companyValid()) return;
    if (!(this.button instanceof HTMLButtonElement) || this.button.hasAttribute('aria-busy')) return;

    const selection = this.selection.filter((mug) => mug.qty > 0);
    const total = selection.reduce((sum, mug) => sum + mug.qty, 0);
    const bulk = total >= this.threshold;
    const packageLine = this.#packageLine();

    if (!packageLine.id) {
      this.#setStatus(s.error || '', 'error');
      return;
    }

    const items = [packageLine];
    for (const mug of selection) {
      /** @type {Record<string, string>} */
      const properties = { _upsell: this.dataset.upsellValue || 'juliet' };
      if (bulk && !mug.extra && this.dataset.leadTimeName) {
        properties[this.dataset.leadTimeName] = this.dataset.leadTimeValue || '';
      }
      items.push({ id: mug.id, quantity: mug.qty, properties });
    }

    // Tea tins: one-time line (never a selling plan), only from the popup's tins step.
    const tinRow = this.querySelector('[data-tin-row]');
    if (this.tinQty > 0 && tinRow instanceof HTMLElement && tinRow.dataset.variantId) {
      items.push({
        id: tinRow.dataset.variantId,
        quantity: this.tinQty,
        properties: { _upsell: this.dataset.upsellValue || 'juliet' },
      });
    }
    this.tinQty = 0;

    const labelEl = this.button.querySelector('[data-label]');
    const previousLabel = labelEl?.textContent || '';
    this.button.setAttribute('aria-busy', 'true');
    this.button.disabled = true;
    if (labelEl) labelEl.textContent = s.adding || previousLabel;
    this.#setStatus('', 'info');

    try {
      await addItemsToCart(this, items);
      this.#setStatus(s.added || '', 'success');
    } catch (error) {
      const message = error instanceof Error && error.message ? error.message : s.error || '';
      this.#setStatus(message, 'error');
    } finally {
      this.button.removeAttribute('aria-busy');
      this.button.disabled = false;
      if (labelEl) labelEl.textContent = previousLabel;
      this.update();
    }
  }

  /**
   * @param {string} message
   * @param {'info' | 'success' | 'error'} type
   */
  #setStatus(message, type) {
    if (!(this.status instanceof HTMLElement)) return;
    this.status.dataset.type = type;
    this.status.hidden = !message;
    const text = this.status.querySelector('[data-status-text]');
    if (text) text.textContent = message;
    const link = this.status.querySelector('[data-status-link]');
    if (link instanceof HTMLElement) link.hidden = type !== 'success';
  }
}

/**
 * B2C: subscription + optional one-time Juliet mug on the subscription landing page.
 */
class HtJulietUpsell extends HTMLElement {
  connectedCallback() {
    this.strings = readStrings(this);
    this.percent = Number(this.dataset.percent || 20);
    this.currency = this.dataset.currency || 'EUR';
    this.locale = this.dataset.locale || document.documentElement.lang;
    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);
    this.update();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('click', this.#onClick);
  }

  #onChange = () => this.update();

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-ht-action]') : null;
    if (!(target instanceof HTMLButtonElement)) return;
    event.preventDefault();
    this.submit(target, target.dataset.htAction === 'mug-only');
  };

  get #mugSelect() {
    const el = this.querySelector('[data-mug-select]');
    return el instanceof HTMLSelectElement ? el : null;
  }

  get #wantsMug() {
    const checkbox = this.querySelector('[data-mug-toggle]');
    return checkbox instanceof HTMLInputElement ? checkbox.checked : false;
  }

  update() {
    const option = this.#mugSelect?.selectedOptions[0];
    const price = Number(option?.dataset.price || this.dataset.mugPrice || 0);
    const money = (/** @type {number} */ cents) => formatMoney(cents, this.currency, this.locale);
    const s = this.strings;

    const label = this.querySelector('[data-mug-label]');
    if (label) label.textContent = (s.addMugFor || '').replace('[price]', money(discounted(price, this.percent)));
    const compare = this.querySelector('[data-mug-compare]');
    if (compare) compare.textContent = (s.insteadOf || '').replace('[price]', money(price));

    const colourWrapper = this.querySelector('[data-mug-colour]');
    if (colourWrapper instanceof HTMLElement) colourWrapper.hidden = !this.#wantsMug;

    const mainButtonLabel = this.querySelector('[data-ht-action="subscription"] [data-label]');
    if (mainButtonLabel) mainButtonLabel.textContent = this.#wantsMug ? s.startWithMug || '' : s.start || '';

    const mugOnly = this.querySelector('[data-ht-action="mug-only"]');
    if (mugOnly instanceof HTMLElement) mugOnly.hidden = !this.#wantsMug;
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {boolean} mugOnly
   */
  async submit(button, mugOnly) {
    const s = this.strings;
    if (button.hasAttribute('aria-busy')) return;

    /** @type {Array<Record<string, any>>} */
    const items = [];

    if (!mugOnly) {
      const variantSelect = this.querySelector('[data-subscription-variant]');
      const planSelect = this.querySelector('[data-selling-plan]');
      /** @type {Record<string, any>} */
      const line = {
        id: variantSelect instanceof HTMLSelectElement ? variantSelect.value : this.dataset.subscriptionVariantId,
        quantity: 1,
      };
      const plan = planSelect instanceof HTMLSelectElement || planSelect instanceof HTMLInputElement ? planSelect.value : '';
      if (plan) line.selling_plan = plan;
      if (line.id) items.push(line);
    }

    const mugVariant = this.#mugSelect?.value;
    if (this.#wantsMug && mugVariant) {
      items.push({ id: mugVariant, quantity: 1, properties: { _upsell: this.dataset.upsellValue || 'juliet' } });
    }

    if (!items.length) return;

    const labelEl = button.querySelector('[data-label]');
    const previousLabel = labelEl?.textContent || '';
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    if (labelEl) labelEl.textContent = s.adding || previousLabel;

    const status = this.querySelector('[data-ht-status]');
    const setStatus = (/** @type {string} */ message, /** @type {string} */ type) => {
      if (!(status instanceof HTMLElement)) return;
      status.dataset.type = type;
      status.hidden = !message;
      const text = status.querySelector('[data-status-text]');
      if (text) text.textContent = message;
      const link = status.querySelector('[data-status-link]');
      if (link instanceof HTMLElement) link.hidden = type !== 'success';
    };
    setStatus('', 'info');

    try {
      await addItemsToCart(this, items);
      setStatus(s.added || '', 'success');
    } catch (error) {
      setStatus(error instanceof Error && error.message ? error.message : s.error || '', 'error');
    } finally {
      button.removeAttribute('aria-busy');
      button.disabled = false;
      if (labelEl) labelEl.textContent = previousLabel;
      this.update();
    }
  }
}

if (!customElements.get('ht-juliet-mug-selector')) {
  customElements.define('ht-juliet-mug-selector', HtJulietMugSelector);
}
if (!customElements.get('ht-juliet-upsell')) {
  customElements.define('ht-juliet-upsell', HtJulietUpsell);
}
